import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@/api/client';
import { fetchAanwezigheid, setMyAanwezig, type Aanwezigheid } from '@/api/administratie';
import { WorkerAddPicker } from '@/components/werkers/WorkerAddPicker';
import { formatDateKey } from '@/utils/planning';

/**
 * The subcontractor's workers and who was present per task, for one day. Only
 * logins with worker contacts get anything back; everyone else gets an empty
 * list and sees no picker.
 */
export function useAanwezigheid(date: string) {
  const [data, setData] = useState<Aanwezigheid | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    fetchAanwezigheid(date)
      .then((d) => { if (!cancelled) setData(d); })
      // Presence is extra: a failure must not get in the way of the planning.
      .catch(() => { if (!cancelled) setData({ workers: [], tasks: [], editable: false }); });
    return () => { cancelled = true; };
  }, [date]);

  /**
   * Per task+worker: what Odoo last confirmed, what the user wants now, and
   * whether a write is on its way. One write at a time per pair, so two quick
   * taps can never land in Odoo in the wrong order: the next write goes out
   * only after the previous one answered, and always carries the latest choice.
   */
  const pending = useRef(new Map<string, { inFlight: boolean; desired: boolean; confirmed: boolean }>());
  useEffect(() => { pending.current.clear(); }, [date]);

  const toggle = useCallback(async (taskId: number, workerId: number, value: boolean) => {
    const apply = (on: boolean) => setData((d) => d && {
      ...d,
      tasks: d.tasks.some((t) => t.taskId === taskId)
        ? d.tasks.map((t) => (t.taskId === taskId
          ? { ...t, present: on ? [...new Set([...t.present, workerId])] : t.present.filter((id) => id !== workerId) }
          : t))
        : [...d.tasks, { taskId, present: on ? [workerId] : [] }],
    });
    const key = `${taskId}-${workerId}`;
    let p = pending.current.get(key);
    // First tap: what was on screen before it is what Odoo has.
    if (!p) {
      p = { inFlight: false, desired: !value, confirmed: !value };
      pending.current.set(key, p);
    }
    p.desired = value;
    setError(null);
    apply(value);
    if (p.inFlight) return; // the running loop sends the latest choice next

    p.inFlight = true;
    while (p.desired !== p.confirmed) {
      const sending = p.desired;
      try {
        await setMyAanwezig(taskId, workerId, sending);
        p.confirmed = sending;
      } catch (err) {
        // A newer tap is waiting: send that instead of undoing it.
        if (p.desired !== sending) continue;
        p.desired = p.confirmed;
        apply(p.confirmed);
        setError(err instanceof ApiError && err.message ? err.message : 'Aanwezigheid opslaan mislukt');
        break;
      }
    }
    p.inFlight = false;
  }, []);

  return { data, error, toggle };
}

/**
 * The question for one day: asked before the work today, looked back on for a past day.
 */
export function presenceQuestion(date: string, today: string = formatDateKey(new Date())): string {
  if (date === today) return 'Wie werkt hier vandaag?';
  return date < today ? 'Wie werkte hier?' : 'Wie werkt hier?';
}

/**
 * "Wie werkt hier vandaag?" under one task card, filled in before the work: the
 * workers marked present as chips with an ×, and "+ medewerker" (red while nobody
 * is marked) to add one from the company's list (with search for a big team).
 * Read-only outside the date window.
 */
export function WerkersPicker({
  taskId,
  date,
  aanwezigheid,
  error,
  onToggle,
}: {
  taskId: number;
  /** The day shown (YYYY-MM-DD): it picks the tense of the question. */
  date: string;
  aanwezigheid: Aanwezigheid;
  error: string | null;
  onToggle: (taskId: number, workerId: number, value: boolean) => void;
}) {
  if (aanwezigheid.workers.length === 0) return null;
  const presentIds = new Set(aanwezigheid.tasks.find((t) => t.taskId === taskId)?.present ?? []);
  const present = aanwezigheid.workers.filter((w) => presentIds.has(w.id));
  const editable = aanwezigheid.editable;

  return (
    <div className="px-4 pb-3" data-testid="werkers-picker">
      <p className="text-sm font-semibold text-td-navy">{presenceQuestion(date)}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {present.map((w) => (
          <span key={w.id} className="inline-flex items-center min-h-[36px] pl-3 pr-1 rounded-full bg-td-blue text-white text-sm" data-testid="werker-chip">
            {w.name}
            {editable && (
              <button
                type="button"
                onClick={() => onToggle(taskId, w.id, false)}
                aria-label={`${w.name} weghalen`}
                className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-white/20"
              >
                ×
              </button>
            )}
          </span>
        ))}
        {editable && (
          <WorkerAddPicker workers={aanwezigheid.workers} presentIds={presentIds} onPick={(id) => onToggle(taskId, id, true)} alert={present.length === 0} />
        )}
      </div>
      {!editable && <p className="mt-1 text-xs text-td-muted">Alleen voor vandaag of de voorbije 30 dagen.</p>}
      {error && <p className="mt-1 text-xs text-red-600" role="alert">{error}</p>}
    </div>
  );
}
